// All data is mocked in-memory. No backend / network calls.

export const NAV_LINKS = [
  { label: "Services", to: "/services" },
  { label: "Contact", to: "/contact" },
];

// Elikya Technology product suite — each links to its own external domain.
export const PRODUCTS = [
  {
    name: "Girdercore",
    url: "https://girdercore.com",
    tag: "Core platform engine",
    desc: "The high-performance proprietary engine that powers everything Elikya Technology builds.",
    icon: "Boxes",
  },
  {
    name: "NightOwl",
    url: "https://nightowl.com",
    tag: "Monitoring & compliance",
    desc: "Intelligent, always-on monitoring and compliance to keep operations safe and audit-ready.",
    icon: "Eye",
  },
  {
    name: "Zando",
    url: "https://zando.co",
    tag: "Community commerce",
    desc: "Accessible merchant and community commerce tools that bring everyone into the economy.",
    icon: "Store",
  },
];

export const VALUE_BADGES = [
  "99.999% Reliability",
  "Zero Settlement Delays",
  "Bank-Grade Protection",
];

export const TRUST_METRICS = [
  { k: "$100M+", v: "annualized volume processed" },
  { k: "10,000+", v: "growth businesses" },
  { k: "99.999%", v: "platform uptime" },
  { k: "2", v: "regional operating hubs" },
];

// Hero "Integrated Payment Ecosystem Core" — 5 peripheral nodes around the money core.
// Positions use a 0–100 viewBox, center at (50,50), radius 38.
export const ECOSYSTEM_NODES = [
  { key: "ecom", label: "E-Commerce Platforms", short: "E-Commerce", icon: "ShoppingBag", desc: "Online store checkout & cart integrations", settle: "T+0 · 1.1s", success: "99.4%", fees: "1.5% + $0.20", x: 50, y: 12,
    coverage: "Hosted checkout, cart plugins and shareable payment links that drop into any online store or marketplace.",
    useCases: ["Online stores & carts", "Subscription billing", "Digital goods & downloads", "Marketplace payouts"] },
  { key: "banks", label: "Regional Banks", short: "Banks", icon: "Landmark", desc: "Direct bank transfers & interbank settlement", settle: "T+0 · 2.3s", success: "99.1%", fees: "0.8% (capped)", x: 86, y: 38,
    coverage: "Direct connections to regional banks for inbound transfers, interbank settlement and bulk disbursement.",
    useCases: ["Bank transfers & collections", "Payroll & disbursements", "Enterprise settlement", "Treasury sweeps"] },
  { key: "cards", label: "Card Schemes", short: "Cards", icon: "CreditCard", desc: "Credit, debit and prepaid card processing", settle: "T+0 · 0.9s", success: "98.8%", fees: "2.4% + $0.15", x: 72, y: 81,
    coverage: "Credit, debit and prepaid processing across major card schemes with tokenised, PCI-safe handling.",
    useCases: ["Card checkout", "Recurring card billing", "Tap-to-pay", "Saved cards & one-click"] },
  { key: "mobile", label: "Mobile & Phone Money", short: "Mobile Money", icon: "Smartphone", desc: "USSD, mobile wallets & phone-based transfers", settle: "T+0 · 1.4s", success: "99.6%", fees: "1.2%", x: 28, y: 81,
    coverage: "USSD menus, mobile wallets and phone-number transfers across every major mobile network.",
    useCases: ["Mobile wallet top-ups", "USSD payments", "Peer-to-peer transfers", "Airtime & bill pay"] },
  { key: "pos", label: "Point-of-Sale Terminals", short: "POS", icon: "Store", desc: "In-person merchant commerce & billing", settle: "T+0 · 1.0s", success: "99.3%", fees: "1.6% + $0.10", x: 14, y: 38,
    coverage: "Smart in-person terminals and soft-POS for physical commerce, with receipts and real-time reconciliation.",
    useCases: ["Retail checkout", "Restaurants & hospitality", "Field & mobile sales", "Multi-lane stores"] },
];

export const VALUE_HIGHLIGHTS = [
  {
    icon: "Globe2",
    title: "Unmatched Accessibility",
    desc: "Accept payments online, in-store or over mobile networks — meet your customers wherever they already are.",
    metric: "Online · In-store · Mobile",
  },
  {
    icon: "ShieldCheck",
    title: "High Reliability",
    desc: "Zero downtime infrastructure so a payment never fails and you never lose a sale at the moment of checkout.",
    metric: "99.999% uptime",
  },
  {
    icon: "Zap",
    title: "Zero Friction",
    desc: "Go live in minutes, not months. No complex onboarding, no heavy paperwork, no barriers to getting paid.",
    metric: "Live in minutes",
  },
  {
    icon: "MapPin",
    title: "Local & Global Reach",
    desc: "Connect directly to regional banks and mobile money, then expand into global markets on the same platform.",
    metric: "Local + global rails",
  },
];

export const FAQS = [
  {
    q: "How fast are settlements?",
    a: "Funds settle on the same day (T+0) across supported rails, with most transactions confirming in under two seconds. Instant payout to your account is available on demand from your dashboard.",
  },
  {
    q: "Which payout regions do you support?",
    a: "Elikya Technology operates regional hubs in South Africa and DR Congo, with direct connections to regional banks and mobile money, plus global payout rails for international expansion.",
  },
  {
    q: "How do you handle security and compliance?",
    a: "Every account includes PCI DSS certified handling, bank-grade encryption, built-in fraud screening and audit-ready records — monitored continuously so you stay protected and compliant.",
  },
  {
    q: "How long does it take to get started?",
    a: "Most businesses go live in minutes. Create a business account, choose your payout region and start accepting payments — no complex onboarding or heavy paperwork required.",
  },
  {
    q: "Is there a long-term contract?",
    a: "No. Start processing on flexible terms with transparent pricing and no lock-in. Scale up or down as your business grows across markets.",
  },
];

export const SERVICE_MODULES = [
  {
    id: "01",
    slug: "turnkey-payments",
    icon: "Wallet",
    title: "Turnkey Payment Systems",
    desc: "Launch custom checkout experiences and branded digital wallets without building payment plumbing from scratch.",
    points: ["Custom checkout", "Branded wallets", "One-tap payments", "Stored balances"],
  },
  {
    id: "02",
    slug: "bank-mobile-money",
    icon: "Network",
    title: "Bank & Mobile Money Integration",
    desc: "A single connection to accept cards, mobile wallets and direct bank transfers across every market you serve.",
    points: ["Cards & mobile money", "Direct bank transfers", "Local payout rails", "Unified reporting"],
  },
  {
    id: "03",
    slug: "security-compliance",
    icon: "Lock",
    title: "Bank-Grade Security & Compliance",
    desc: "Built-in fraud detection, PCI DSS compliance and audit protection safeguard every transaction and record.",
    points: ["Fraud detection", "PCI DSS compliant", "Audit protection", "Encryption everywhere"],
  },
  {
    id: "04",
    slug: "cloud-infrastructure",
    icon: "ServerCog",
    title: "Always-On Cloud Infrastructure",
    desc: "Automated backup and failover systems guarantee your business keeps accepting money around the clock.",
    points: ["Automated failover", "24/7 availability", "Real-time monitoring", "Disaster recovery"],
  },
];

export const STORIES = [
  {
    name: "Bela Market",
    type: "Retail · South Africa",
    quote: "Customers used to abandon the till when mobile payments failed. Now checkout just works — and they come back.",
    before: "2–3 day settlements, 14% dropped payments",
    after: "Same-day payouts, under 2% drop-off",
    metric: "+32% repeat buyers",
  },
  {
    name: "Kito Logistics",
    type: "Delivery · DR Congo",
    quote: "Collecting cash on delivery was chaos. Accepting mobile money and cards on one terminal changed everything.",
    before: "Cash-only, manual tracking",
    after: "Mobile money + card on one device",
    metric: "+$140k monthly volume",
  },
  {
    name: "Nuru Wholesale",
    type: "B2B commerce · Kenya",
    quote: "Month-end reconciliation took two staff days. The real-time ledger now does it while we sleep.",
    before: "11 hrs/week reconciling by hand",
    after: "Auto-reconciled, audit-ready ledger",
    metric: "11 hrs/week saved",
  },
];

export const OUTCOMES = [
  { icon: "TrendingUp", stat: "+38%", label: "Average lift in checkout conversion" },
  { icon: "Clock", stat: "6 days", label: "Faster from sign-up to first payment" },
  { icon: "ShieldCheck", stat: "–72%", label: "Reduction in fraudulent chargebacks" },
  { icon: "Banknote", stat: "T+0", label: "Same-day settlement to your account" },
];

export const TRUST_MARQUEE = [
  "Retail Merchants",
  "Marketplaces",
  "Fintechs",
  "Logistics",
  "Telecom Operators",
  "Microfinance",
  "E-commerce",
  "Utilities & Billers",
  "Hospitality",
  "SaaS Platforms",
];

const customers = ["Amara O.", "Thabo M.", "Chen Wei", "Grace N.", "Kwame A.", "Zola D.", "Ravi P.", "Fatou S.", "Lebo K.", "Omar H."];
const methods = ["Mobile Money", "Bank Transfer", "Card", "USSD", "POS Terminal"];
const statuses = ["Paid", "Paid", "Paid", "Pending", "Paid", "Refunded"];
const currencies = ["USD", "CDF", "ZAR", "KES", "NGN", "GHS"];

function rand(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

export function generatePayments(count = 8) {
  const now = Date.now();
  return Array.from({ length: count }).map((_, i) => {
    const amount = (Math.random() * 1800 + 25).toFixed(2);
    const ts = new Date(now - i * (Math.random() * 3600000 + 600000));
    return {
      id: `INV-${Math.floor(Math.random() * 9000 + 1000)}`,
      customer: rand(customers),
      amount,
      currency: rand(currencies),
      method: rand(methods),
      status: rand(statuses),
      date: ts.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      time: ts.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
    };
  });
}

export const PAYOUT_REGIONS = [
  "DR Congo",
  "South Africa",
  "Nigeria",
  "Kenya",
  "Ghana",
  "Pan-African",
  "Global",
];

export const REVENUE_VOLUMES = [
  "< $50k / mo",
  "$50k – $500k / mo",
  "$500k – $5M / mo",
  "$5M+ / mo",
];

export const SOLUTION_DETAILS = {
  "turnkey-payments": {
    slug: "turnkey-payments",
    icon: "Wallet",
    eyebrow: "Launch fast",
    title: "Turnkey Payment Systems",
    subtitle: "Custom checkout experiences and branded digital wallets — live in days, not quarters.",
    overview:
      "Stand up a complete payments experience without building a single piece of infrastructure. From a hosted checkout to a fully branded wallet app, every component is production-ready, secure and configurable to your brand on day one.",
    capabilities: [
      { icon: "CreditCard", title: "Hosted & embedded checkout", desc: "Drop-in checkout or fully embedded flows that match your brand and convert on every device." },
      { icon: "Wallet", title: "Branded digital wallets", desc: "Launch stored-value wallets and apps on top of a real-time ledger, no rebuild required." },
      { icon: "Zap", title: "One-tap payments", desc: "Saved payment methods and one-tap repeat purchases to lift returning-customer conversion." },
      { icon: "Receipt", title: "Billing & invoicing", desc: "Send branded invoices and recurring bills with automatic reminders and reconciliation." },
    ],
    outcomes: [
      { stat: "Days", label: "From kickoff to live checkout" },
      { stat: "+38%", label: "Average conversion lift" },
      { stat: "1-tap", label: "Returning-customer checkout" },
    ],
    faqs: [
      { q: "Can we match our brand?", a: "Yes — colors, logo, domain and copy are all configurable, and embedded flows inherit your own styling." },
      { q: "Do we need to handle card data?", a: "No. Sensitive data is tokenised and never touches your servers, keeping you in the lightest PCI scope." },
    ],
  },
  "bank-mobile-money": {
    slug: "bank-mobile-money",
    icon: "Network",
    eyebrow: "One connection",
    title: "Bank & Mobile Money Integration",
    subtitle: "A single integration to accept cards, mobile wallets and direct bank transfers everywhere you operate.",
    overview:
      "African payment rails are fragmented — we normalise them. Connect once and reach regional banks, every major mobile money network and card schemes through one consistent API and one unified reconciliation view.",
    capabilities: [
      { icon: "Smartphone", title: "Mobile money & USSD", desc: "Accept wallet payments, USSD flows and phone-number transfers across all major networks." },
      { icon: "Landmark", title: "Direct bank transfers", desc: "Pull-in collections and bulk payouts with direct bank and interbank settlement." },
      { icon: "CreditCard", title: "Card acceptance", desc: "Credit, debit and prepaid processing with smart routing for the best approval rates." },
      { icon: "Globe2", title: "Unified reporting", desc: "Every rail reconciled into one ledger and one dashboard — no more spreadsheet stitching." },
    ],
    outcomes: [
      { stat: "28+", label: "Payment methods on one API" },
      { stat: "T+0", label: "Same-day settlement" },
      { stat: "99.2%", label: "Blended authorisation rate" },
    ],
    faqs: [
      { q: "How many networks do you support?", a: "All major mobile money operators, regional banks and global card schemes across our operating markets." },
      { q: "Is it one contract?", a: "Yes — a single agreement and single integration gives you access to every connected rail." },
    ],
  },
  "security-compliance": {
    slug: "security-compliance",
    icon: "Lock",
    eyebrow: "Protected by default",
    title: "Bank-Grade Security & Compliance",
    subtitle: "Fraud detection, PCI DSS compliance and audit protection built into every account.",
    overview:
      "Security isn't an add-on — it's the foundation. Every transaction is encrypted, screened and logged against tamper-evident records, so you stay protected, compliant and audit-ready without hiring a security team.",
    capabilities: [
      { icon: "ShieldCheck", title: "Real-time fraud detection", desc: "Machine-assisted screening scores every transaction and blocks threats before they settle." },
      { icon: "Lock", title: "PCI DSS Level 1", desc: "Certified handling and tokenisation keep you in the lightest possible compliance scope." },
      { icon: "Eye", title: "Continuous monitoring", desc: "Always-on monitoring surfaces anomalies and keeps a close watch on every rail." },
      { icon: "Receipt", title: "Audit-ready records", desc: "Immutable, exportable logs make audits and reconciliations straightforward." },
    ],
    outcomes: [
      { stat: "–72%", label: "Reduction in chargebacks" },
      { stat: "AES-256", label: "Encryption at rest" },
      { stat: "100%", label: "Audit-ready transactions" },
    ],
    faqs: [
      { q: "Are you PCI compliant?", a: "Yes, we operate at PCI DSS Level 1, the highest tier, and tokenise sensitive data end to end." },
      { q: "How do you handle fraud?", a: "Every transaction is risk-scored in real time with configurable rules and automatic blocking." },
    ],
  },
  "cloud-infrastructure": {
    slug: "cloud-infrastructure",
    icon: "ServerCog",
    eyebrow: "Always on",
    title: "Always-On Cloud Infrastructure",
    subtitle: "Automated failover and backup systems that keep your business accepting money 24/7.",
    overview:
      "Downtime means lost sales. Our active-active infrastructure, automated failover and continuous backups are engineered for five-nines of uptime, so your checkout keeps working even when something, somewhere, breaks.",
    capabilities: [
      { icon: "ServerCog", title: "Active-active failover", desc: "Traffic reroutes automatically across regions with zero manual intervention." },
      { icon: "Clock", title: "99.999% uptime SLA", desc: "Five-nines availability backed by a contractual service-level agreement." },
      { icon: "Eye", title: "Real-time observability", desc: "Live monitoring and alerting catch issues before they ever reach your customers." },
      { icon: "Zap", title: "Zero-downtime deploys", desc: "Continuous updates ship behind the scenes without interrupting a single payment." },
    ],
    outcomes: [
      { stat: "99.999%", label: "Platform uptime SLA" },
      { stat: "24/7", label: "Availability" },
      { stat: "0", label: "Planned-maintenance downtime" },
    ],
    faqs: [
      { q: "What uptime do you guarantee?", a: "A contractual 99.999% uptime SLA, backed by active-active infrastructure and automated failover." },
      { q: "Will updates cause downtime?", a: "No. We deploy continuously with zero-downtime releases, so payments never pause." },
    ],
  },
};

export const SOLUTION_ORDER = [
  "turnkey-payments",
  "bank-mobile-money",
  "security-compliance",
  "cloud-infrastructure",
];
